import { useUsdcToCad } from '@/hooks/useCpn'
import { convertUsdcToCad } from '@/utils/currencyUtils'
import { formatCurrency } from '@/utils/payment'

// Component to display USDC amount converted to CAD
export const AmountInCad = ({
  usdcAmount,
  originalCurrency,
  cadUsdcRate,
  fallbackToHook = false,
}: {
  usdcAmount: number
  originalCurrency: string
  cadUsdcRate?: number
  fallbackToHook?: boolean
}) => {
  // Fallback to old hook behavior if cadUsdcRate not provided and fallbackToHook is true
  const { cadAmount: hookCadAmount, isLoading } = useUsdcToCad(
    usdcAmount.toString(),
    {
      enabled: fallbackToHook && originalCurrency === 'USDC' && !cadUsdcRate,
    }
  )

  if (originalCurrency !== 'USDC') {
    return formatCurrency(usdcAmount, originalCurrency)
  }

  if (fallbackToHook && !cadUsdcRate && isLoading) {
    return 'Loading...'
  }

  // Use provided exchange rate if available, otherwise fall back to hook result
  const cadAmount = cadUsdcRate
    ? convertUsdcToCad(usdcAmount, cadUsdcRate)
    : hookCadAmount

  // Always return CAD - use a fallback rate if conversion fails
  if (cadAmount) {
    return formatCurrency(cadAmount, 'CAD')
  }

  // If no conversion available, use a fallback rate (1.38 CAD per USDC)
  const fallbackCadAmount = usdcAmount / 1.38
  return formatCurrency(fallbackCadAmount, 'CAD')
}

// Component to convert USDC payment amounts to CAD for dashboard display
export const PaymentAmountInCad = ({
  usdcAmount,
  cadUsdcRate,
  fallbackToHook = false,
}: {
  usdcAmount: number
  cadUsdcRate?: number
  fallbackToHook?: boolean
}) => {
  // Fallback to old hook behavior if cadUsdcRate not provided and fallbackToHook is true
  const { cadAmount: hookCadAmount, isLoading } = useUsdcToCad(
    usdcAmount.toString(),
    {
      enabled: fallbackToHook && !cadUsdcRate,
    }
  )

  if (fallbackToHook && !cadUsdcRate && isLoading) {
    return (
      <span className="font-semibold text-gray-900 dark:text-white text-sm">
        Loading...
      </span>
    )
  }

  // Use provided exchange rate if available, otherwise fall back to hook result
  const cadAmount = cadUsdcRate
    ? convertUsdcToCad(usdcAmount, cadUsdcRate)
    : hookCadAmount

  // Always prefer CAD if available
  if (cadAmount) {
    return (
      <span className="font-semibold text-gray-900 dark:text-white text-sm">
        $
        {cadAmount.toLocaleString('en-CA', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}{' '}
        CAD
      </span>
    )
  }

  // Fallback to USDC
  return (
    <span className="font-semibold text-gray-900 dark:text-white text-sm">
      $
      {usdcAmount.toLocaleString('en-CA', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}{' '}
      USDC
    </span>
  )
}

// Component to convert total sent amount to CAD for dashboard stats
export const TotalSentInCad = ({
  usdcTotal,
  cadUsdcRate,
  fallbackToHook = false,
}: {
  usdcTotal: number
  cadUsdcRate?: number
  fallbackToHook?: boolean
}) => {
  // Fallback to old hook behavior if cadUsdcRate not provided and fallbackToHook is true
  const { cadAmount: hookCadAmount, isLoading } = useUsdcToCad(
    usdcTotal.toString(),
    {
      enabled: fallbackToHook && !cadUsdcRate && usdcTotal > 0,
    }
  )

  if (fallbackToHook && !cadUsdcRate && isLoading) {
    return '...'
  }

  // Use provided exchange rate if available, otherwise fall back to hook result
  const cadAmount = cadUsdcRate
    ? convertUsdcToCad(usdcTotal, cadUsdcRate)
    : hookCadAmount

  // Always prefer CAD if available, otherwise show USDC with a note
  if (cadAmount) {
    return `$${cadAmount.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} CAD`
  }

  // Fallback to USDC if conversion failed
  if (usdcTotal > 0) {
    return `$${usdcTotal.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USDC`
  }

  return '$0.00 CAD'
}
